import { useLayoutEffect, useRef, useState } from 'react'

import { cn } from '#/shared/lib/utils'

export const SEGMENTED_TAB_VARIANTS = {
  PILL: 'pill',
  SOFT: 'soft',
} as const

export type SegmentedTabsVariant =
  (typeof SEGMENTED_TAB_VARIANTS)[keyof typeof SEGMENTED_TAB_VARIANTS]

const TRACK_CLASS_BY_VARIANT: Record<SegmentedTabsVariant, string> = {
  [SEGMENTED_TAB_VARIANTS.PILL]: 'gap-0.5 rounded-lg bg-gray-100 p-1',
  [SEGMENTED_TAB_VARIANTS.SOFT]: 'gap-1',
}

const INDICATOR_CLASS_BY_VARIANT: Record<SegmentedTabsVariant, string> = {
  [SEGMENTED_TAB_VARIANTS.PILL]:
    'top-1 bottom-1 rounded-md border border-gray-200 bg-white shadow-sm',
  [SEGMENTED_TAB_VARIANTS.SOFT]: 'inset-y-0 rounded-md bg-gray-100',
}

export interface SegmentedTabItem<TValue extends string> {
  value: TValue
  render: () => React.ReactNode
}

export interface SegmentedTabsProps<TValue extends string> {
  items: ReadonlyArray<SegmentedTabItem<TValue>>
  value: TValue
  onValueChange: (next: TValue) => void
  variant?: SegmentedTabsVariant
  /** Stretch the track and divide it into equal-width segments. */
  fullWidth?: boolean
  className?: string
}

interface IndicatorStyle {
  left: number
  width: number
  ready: boolean
}

export function SegmentedTabs<TValue extends string>({
  items,
  value,
  onValueChange,
  variant = SEGMENTED_TAB_VARIANTS.PILL,
  fullWidth = false,
  className,
}: SegmentedTabsProps<TValue>) {
  const buttonRefs = useRef<Map<TValue, HTMLButtonElement>>(new Map())
  const [indicator, setIndicator] = useState<IndicatorStyle>({
    left: 0,
    width: 0,
    ready: false,
  })

  useLayoutEffect(() => {
    const node = buttonRefs.current.get(value)
    if (!node) return
    setIndicator({
      left: node.offsetLeft,
      width: node.offsetWidth,
      ready: true,
    })
  }, [value, items])

  return (
    <div
      role="tablist"
      className={cn(
        'relative flex items-center',
        TRACK_CLASS_BY_VARIANT[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute transition-[left,width] duration-200 ease-out',
          INDICATOR_CLASS_BY_VARIANT[variant],
        )}
        style={{
          left: indicator.left,
          width: indicator.width,
          opacity: indicator.ready ? 1 : 0,
        }}
      />
      {items.map((item) => {
        const isActive = item.value === value
        return (
          <button
            key={item.value}
            ref={(node) => {
              if (node) buttonRefs.current.set(item.value, node)
              else buttonRefs.current.delete(item.value)
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onValueChange(item.value)}
            className={cn(
              'relative z-10 flex cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors',
              fullWidth && 'flex-1 justify-center',
              isActive ? 'text-gray-900' : 'text-gray-500 hover:text-gray-900',
            )}
          >
            {item.render()}
          </button>
        )
      })}
    </div>
  )
}
